import axios from 'axios';

// ---------------------------------------------------------------------------
// Host failover:
// Several Indian ISPs (notably Jio) block / DNS-poison api.themoviedb.org, so
// every direct call dies at the network level and the catalogs come back
// empty. TMDB's own mirror api.tmdb.org serves the identical API and is
// normally not blocked, so each request falls through the bases below until
// one answers. REACT_APP_TMDB_PROXY (optional, baked in at build time) is
// tried first — point it at a full API root (e.g.
// https://my-proxy.example.com/3) that forwards to api.themoviedb.org/3.
// ---------------------------------------------------------------------------
const TMDB_BASE_HOST = 'https://api.themoviedb.org';
const TMDB_MIRROR_HOST = 'https://api.tmdb.org';

const TMDB_BASE_URL = `${TMDB_BASE_HOST}/3`;

// Bases are full API roots (origin + /3); requestWithRetry appends the path.
const TMDB_API_BASES = [
  ...(process.env.REACT_APP_TMDB_PROXY ? [process.env.REACT_APP_TMDB_PROXY] : []),
  TMDB_BASE_URL,
  `${TMDB_MIRROR_HOST}/3`,
];

// Index of the last base that answered successfully — sticky, so a working
// host is never re-probed by later requests.
let activeBaseIndex = 0;

// Test hook: put the failover state back to "primary first".
const resetHostFailover = () => {
  activeBaseIndex = 0;
};

export { TMDB_API_BASES, resetHostFailover };

// Catalog languages.
//
// Pehle sirf ['hi','gu'] the — isliye feed me Gujarati titles ka pool
// overflow ho gaya aur user ko "sirf Gujarati" dikha. Ab chaar lanes:
//   Bollywood      -> hi
//   South Indian   -> ta (Tamil), te (Telugu), kn (Kannada), ml (Malayalam)
//   Hollywood      -> en  (Hindi-dubbed playback; region IN se India release)
//   Gujarati       -> gu  (kept, par quota se control — pehle jitna chahiye tha)
const CATALOG_LANGUAGES = {
  bollywood: ['hi'],
  south: ['ta', 'te', 'kn', 'ml'],
  hollywood: ['en'],
  gujarati: ['gu'],
};

const INDIAN_LANGS = [
  ...CATALOG_LANGUAGES.bollywood,
  ...CATALOG_LANGUAGES.south,
  ...CATALOG_LANGUAGES.hollywood,
  ...CATALOG_LANGUAGES.gujarati,
];

const filterIndian = (items) => (items || []).filter((item) => INDIAN_LANGS.includes(item?.original_language));
export { filterIndian, INDIAN_LANGS, CATALOG_LANGUAGES };

// Default params for catalog list endpoints: Bollywood + South + Hollywood +
// Gujarati, India region. (Comma lists are fine on /movie/popular & co.)
const INDIAN_PARAMS = {
  with_original_language: INDIAN_LANGS.join(','),
  region: 'IN',
};

// ---------------------------------------------------------------------------
// Release-window helpers:
// Catalogs (Home, Movies, Series) must show ONLY released titles —
// future titles belong exclusively to the Upcoming / Coming Soon lane.
// ---------------------------------------------------------------------------
const todayStr = () => new Date().toISOString().slice(0, 10);

const getReleaseDate = (item) => item?.release_date || item?.first_air_date || null;

// Missing date = treat as released so nothing disappears from the catalog
const isReleasedItem = (item) => {
  const date = getReleaseDate(item);
  return !date || date <= todayStr();
};

const filterReleased = (items) => (items || []).filter(isReleasedItem);

const filterUpcoming = (items) =>
  (items || []).filter((item) => {
    const date = getReleaseDate(item);
    return Boolean(date && date > todayStr());
  });

export { isReleasedItem, filterReleased };

// TV payloads use `name` / `first_air_date`; Poster and the feed components expect
// the movie shape. Normalizing here keeps every call site from re-implementing it.
const normalizeTVItem = (tv) => ({
  ...tv,
  media_type: 'tv',
  title: tv.name || tv.original_name,
  original_title: tv.original_name || tv.name,
  release_date: tv.first_air_date,
});

export { normalizeTVItem };

const TIMEOUT_MS = 10000;

const tmdbClient = axios.create({
  baseURL: TMDB_BASE_URL,
  timeout: TIMEOUT_MS,
});

tmdbClient.interceptors.request.use((config) => {
  if (process.env.REACT_APP_API_KEY) {
    config.params = {
      api_key: process.env.REACT_APP_API_KEY,
      ...config.params,
    };
  }
  return config;
});

// Shared by tmdbFeed so the feed reuses the same base URL + api_key interceptor
// instead of instantiating a second client.
export { tmdbClient };

// ---------------------------------------------------------------------------
// Request layer: retry + in-flight de-duplication.
//
// Three failures motivated this:
//   1. A single 10s timeout used to permanently empty the page — no retry, so one
//      blip on a flaky connection looked identical to "no Indian titles".
//   2. React StrictMode double-invokes every mount effect in development, and
//      Home/HeroCarousal both hit /movie/now_playing. Every request was going out
//      twice, which needlessly burns TMDB's per-IP rate limit.
//   3. Some ISPs (Jio et al.) block api.themoviedb.org outright — see the host
//      failover note at the top of the file.
// ---------------------------------------------------------------------------

const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 600;

// Consecutive attempts rotate TMDB_API_BASES, so every host gets
// MAX_RETRIES + 1 shots before the request gives up for good.
const MAX_ATTEMPTS = TMDB_API_BASES.length * (MAX_RETRIES + 1);

const inFlightRequests = {};

const buildRequestKey = (path, params) =>
  `${path}?${Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join('&')}`;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Retry transport failures and 5xx, never a 4xx — a bad API key or an invalid
// genre id will fail identically on the second attempt.
const isRetryable = (err) => {
  const status = err && err.response ? err.response.status : 0;
  return !status || status >= 500;
};

const describeTmdbError = (path, err) => {
  const code = err && err.code === 'ECONNABORTED' ? `timed out after ${TIMEOUT_MS}ms` : (err && err.message) || 'unknown error';
  const status = err && err.response ? err.response.status : null;
  const hint = status
    ? ''
    : ' — the TMDB API hosts look unreachable from this network (api.themoviedb.org may be blocked by your ISP; check VPN/DNS/offline mode).';
  return `TMDB ${path} failed: ${code}${status ? ` (HTTP ${status})` : ''}${hint}`;
};

// An ISP block kills the request with no HTTP status at all, which is why host
// rotation shares the same retryable gate as timeouts and 5xx.
const requestWithRetry = async (path, params, attempt = 0) => {
  const baseIndex = (activeBaseIndex + attempt) % TMDB_API_BASES.length;
  try {
    // Absolute URL — overrides the client's baseURL so one client serves every host.
    const res = await tmdbClient.get(`${TMDB_API_BASES[baseIndex]}${path}`, { params });
    activeBaseIndex = baseIndex; // stick to the host that worked
    return res;
  } catch (err) {
    if (attempt + 1 < MAX_ATTEMPTS && isRetryable(err)) {
      await wait(RETRY_DELAY_MS);
      return requestWithRetry(path, params, attempt + 1);
    }
    throw err;
  }
};

/**
 * Perform a de-duplicated, retrying TMDB request.
 *
 * Concurrent callers asking for the same path + params share one in-flight promise
 * and therefore cost one HTTP request. Rejects on failure so callers can decide
 * between surfacing an error and degrading quietly.
 */
export const tmdbRequest = (path, params = {}) => {
  const key = buildRequestKey(path, params);
  if (inFlightRequests[key]) return inFlightRequests[key];

  const request = requestWithRetry(path, params).then(
    (res) => {
      delete inFlightRequests[key];
      return res;
    },
    (err) => {
      delete inFlightRequests[key];
      throw err;
    }
  );

  inFlightRequests[key] = request;
  return request;
};

export { describeTmdbError };

async function getResults(path, params = {}, transform = (data) => data?.results || [], useIndianParams = false, applyIndianFilter = false) {
  try {
    const res = await tmdbRequest(path, {
      ...(useIndianParams ? INDIAN_PARAMS : {}),
      ...params,
    });
    const data = transform(res?.data);
    return Array.isArray(data) && applyIndianFilter ? filterIndian(data) : data;
  } catch (err) {
    console.error(describeTmdbError(path, err));
    return transform(null);
  }
}

export const tmdbService = {
  // --- Movies Endpoints (Hindi/Gujarati priority, released titles only) ---
  getNowPlaying: async (page = 1) =>
    filterReleased(await getResults('/movie/now_playing', { page }, undefined, true, true)),
  getPopular: async (page = 1) =>
    filterReleased(await getResults('/movie/popular', { page }, undefined, true, true)),
  getTopRated: async (page = 1) =>
    filterReleased(await getResults('/movie/top_rated', { page }, undefined, true, true)),
  getUpcoming: async (page = 1) =>
    filterUpcoming(await getResults('/movie/upcoming', { page }, undefined, true, true)),
  // Trending lane = discover by popularity (TMDB /trending does not honor language filters)
  getTrending: async (timeWindow = 'week', page = 1) =>
    filterReleased(
      await getResults('/discover/movie', { sort_by: 'popularity.desc', page }, undefined, true, true)
    ),
  getLatestMovies: async (page = 1) => {
    const today = todayStr();
    return filterReleased(
      await getResults(
        '/discover/movie',
        {
          sort_by: 'primary_release_date.desc',
          'primary_release_date.lte': today,
          page,
        },
        undefined,
        true,
        true
      )
    );
  },

  getMovieDetails: async (id) => {
    if (!id) return null;
    return getResults(`/movie/${id}`, {}, (data) => data || null);
  },

  getMovieCredits: async (id) => {
    if (!id) return { cast: [], crew: [] };
    return getResults(`/movie/${id}/credits`, {}, (data) => data || { cast: [], crew: [] });
  },

  getMovieVideos: async (id) => {
    if (!id) return [];
    return getResults(`/movie/${id}/videos`);
  },

  getSimilarMovies: async (id) => {
    if (!id) return [];
    return getResults(`/movie/${id}/similar`);
  },

  getRecommendations: async (id, title = '') => {
    if (!id) return [];
    return getResults(`/movie/${id}/recommendations`);
  },

  // Search: NO language filter — user wants all results in search (catalog stays Indian-only).
  searchMovies: async (query) => {
    if (!query || !query.trim()) return [];
    return getResults(
      '/search/movie',
      { query: query.trim() },
      (data) => data?.results || []
    );
  },

  // Search: NO language filter — only people excluded from multi search.
  searchAll: async (query) => {
    if (!query || !query.trim()) return [];
    return getResults(
      '/search/multi',
      { query: query.trim() },
      (data) => (data?.results || []).filter((r) => r.media_type !== 'person')
    );
  },

  // --- TV Series Endpoints (Hindi/Gujarati priority, aired titles only) ---
  getPopularTV: async (page = 1) =>
    filterReleased(await getResults('/tv/popular', { page }, undefined, true, true)),
  getTopRatedTV: async (page = 1) =>
    filterReleased(await getResults('/tv/top_rated', { page }, undefined, true, true)),
  getOnTheAirTV: async (page = 1) =>
    filterReleased(await getResults('/tv/on_the_air', { page }, undefined, true, true)),
  getTrendingTV: async (page = 1) =>
    filterReleased(await getResults('/discover/tv', { sort_by: 'popularity.desc', page }, undefined, true, true)),

  getTVDetails: async (id) => {
    if (!id) return null;
    return getResults(`/tv/${id}`, {}, (data) => data || null);
  },

  getTVCredits: async (id) => {
    if (!id) return { cast: [], crew: [] };
    return getResults(`/tv/${id}/credits`, {}, (data) => data || { cast: [], crew: [] });
  },

  getTVVideos: async (id) => {
    if (!id) return [];
    return getResults(`/tv/${id}/videos`);
  },

  getTVSeason: async (id, season) => {
    if (!id || !season) return { episodes: [] };
    return getResults(`/tv/${id}/season/${season}`, {}, (data) => data || { episodes: [] });
  },

  getSimilarTV: async (id) => {
    if (!id) return [];
    return getResults(`/tv/${id}/similar`);
  },

  // Search: NO language filter — user wants all results in search (catalog stays Indian-only).
  searchTV: async (query) => {
    if (!query || !query.trim()) return [];
    return getResults(
      '/search/tv',
      { query: query.trim() },
      (data) => data?.results || []
    );
  },
};

// --- Watchlist Service (localStorage only; watchlist persisted by the app itself) ---
export const watchlistService = {
  getWatchlist: async () => [],
  addToWatchlist: async () => false,
  removeFromWatchlist: async () => false,
};

export default tmdbService;
